import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX } from "react-icons/hi";

import FilePreviewModal, {
  detectPreviewKind,
  type FilePreviewKind,
} from "components/FilePreviewModal";

import {
  getAssignmentStatus,
  saveAssignmentStatus,
  type AssignmentStatusApplicationItem,
} from "../../api/assignment";
import { generateUploadPresignedUrls } from "../../api/discussion";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

export const ASSIGNMENT_STATUS_OF_ASSIGNMENT_FORM_ID =
  "assignment-status-of-assignment-form";

const ACCEPTED_TYPES = ".pdf";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const UPLOAD_FOLDER_PREFIX = "assignment/status";

const HEADER_CELL = "text-[12px] font-normal leading-5 text-[#6D6D6D]";
const GRID_COLS =
  "grid-cols-[140px_minmax(260px,1fr)_minmax(260px,1fr)]";

type DocumentKind = "intimation" | "status";

type RowState = AssignmentStatusApplicationItem & {
  pendingIntimationFilename?: string | null;
  pendingStatusFilename?: string | null;
};

type PreviewState = {
  url: string;
  name: string;
  kind: FilePreviewKind;
};

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function AssignmentStatusOfAssignmentView({
  leadId,
  onSuccess,
}: Props) {
  const [applications, setApplications] = useState<RowState[]>([]);
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [uploadingKeys, setUploadingKeys] = useState<Record<string, boolean>>(
    {}
  );
  const [preview, setPreview] = useState<PreviewState | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setApplications([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAssignmentStatus(leadId)
      .then((data) => {
        if (cancelled) return;
        setApplications(data.applications ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to load Status of Assignment data"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const rowKey = (applicationId: string, kind: DocumentKind) =>
    `${applicationId}:${kind}`;

  const setUploading = (key: string, value: boolean) => {
    setUploadingKeys((prev) => {
      if (value) return { ...prev, [key]: true };
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const setPendingFilename = (
    applicationId: string,
    kind: DocumentKind,
    filename: string | null
  ) => {
    setApplications((prev) =>
      prev.map((app) =>
        app.applicationId === applicationId
          ? kind === "intimation"
            ? { ...app, pendingIntimationFilename: filename }
            : { ...app, pendingStatusFilename: filename }
          : app
      )
    );
  };

  const uploadFile = async (
    applicationId: string,
    kind: DocumentKind,
    file: File
  ) => {
    if (!leadId) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`${file.name} exceeds the 50MB limit`);
      return;
    }
    const key = rowKey(applicationId, kind);
    setPendingFilename(applicationId, kind, file.name);
    setUploading(key, true);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [{ filename: file.name, mimeType: file.type || "application/pdf" }],
        "private",
        `${UPLOAD_FOLDER_PREFIX}/${leadId}`
      );
      if (!presigned?.url || !presigned?.key) {
        throw new Error("Failed to generate upload URL");
      }

      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      const data = await saveAssignmentStatus(leadId, {
        applications: [
          kind === "intimation"
            ? {
                applicationId,
                assignmentIntimationPdfFileKey: presigned.key,
                assignmentIntimationPdfFilename: file.name,
              }
            : {
                applicationId,
                statusOfApplicationPdfFileKey: presigned.key,
                statusOfApplicationPdfFilename: file.name,
              },
        ],
      });

      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      setPendingFilename(applicationId, kind, null);
      toast.error(err instanceof Error ? err.message : "Failed to upload file");
    } finally {
      setUploading(key, false);
    }
  };

  const removeUploadedPdf = async (
    applicationId: string,
    kind: DocumentKind
  ) => {
    if (!leadId) return;
    try {
      const data = await saveAssignmentStatus(leadId, {
        applications: [
          kind === "intimation"
            ? {
                applicationId,
                assignmentIntimationPdfFileKey: null,
                assignmentIntimationPdfFilename: null,
              }
            : {
                applicationId,
                statusOfApplicationPdfFileKey: null,
                statusOfApplicationPdfFilename: null,
              },
        ],
      });
      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success("File removed.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove file");
    }
  };

  const openPreview = (filename: string | null, url: string | null) => {
    if (!url || !filename) {
      toast.error("Preview is not available yet.");
      return;
    }
    setPreview({ url, name: filename, kind: detectPreviewKind(filename) });
  };

  const closePreview = () => setPreview(null);

  const handleDownload = async (
    filename: string | null,
    url: string | null
  ) => {
    if (!url || !filename) {
      toast.error("No file available to download.");
      return;
    }
    try {
      const result = await downloadFileFromUrlWithFallback(url, filename);
      if (result === "local") {
        toast.success("File saved to your device.");
      } else {
        toast.info(
          "Opened the file in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download file");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (applications.length === 0) {
      toast.error("No applications found. Complete the previous steps first.");
      return;
    }
    setSaving(true);
    try {
      const data = await saveAssignmentStatus(leadId, {
        applications: applications.map((app) => ({
          applicationId: app.applicationId,
        })),
      });
      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success("Status of Assignment saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading Status of Assignment data...</p>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          No applications found. Please complete the Invoice step first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={ASSIGNMENT_STATUS_OF_ASSIGNMENT_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-1.5">
        <p
          className="font-semibold text-[#101111]"
          style={{
            fontSize: "14px",
            lineHeight: "100%",
            letterSpacing: "0.01em",
          }}
        >
          Upload Documents
        </p>
        <p
          className="font-normal text-[#7D7D7D]"
          style={{ fontSize: "12px", lineHeight: "20px", letterSpacing: "0.04em" }}
        >
          Upload the Assignment Intimation PDF and Status of Application PDF
          downloaded from the Trademarks Registry for each application.
        </p>
      </div>

      <div className="self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-3 md:p-4 overflow-x-auto">
        <div className="min-w-[720px] flex flex-col">
          <div className={`grid ${GRID_COLS} gap-3 px-1 pb-2`}>
            <div className={HEADER_CELL}>TM No.</div>
            <div className={HEADER_CELL}>Assignment Intimation PDF</div>
            <div className={HEADER_CELL}>Status of Application PDF</div>
          </div>

          {applications.map((row) => (
            <div
              key={row.applicationId}
              className={`grid ${GRID_COLS} gap-3 px-1 py-3 items-center border-t border-[#EEEEEE] first:border-t-0`}
            >
              <div
                className="text-[12px] leading-5 text-[#222222] truncate"
                title={row.tmNo || ""}
              >
                {row.tmNo || "—"}
              </div>

              <UploadCell
                applicationId={row.applicationId}
                kind="intimation"
                filename={
                  row.pendingIntimationFilename ??
                  row.assignmentIntimationPdfFilename ??
                  null
                }
                downloadUrl={row.assignmentIntimationDownloadUrl}
                uploadedFilename={row.assignmentIntimationPdfFilename}
                uploading={
                  !!uploadingKeys[rowKey(row.applicationId, "intimation")]
                }
                onFileSelected={(file) =>
                  void uploadFile(row.applicationId, "intimation", file)
                }
                onPreview={() =>
                  openPreview(
                    row.assignmentIntimationPdfFilename,
                    row.assignmentIntimationDownloadUrl
                  )
                }
                onDownload={() =>
                  void handleDownload(
                    row.assignmentIntimationPdfFilename,
                    row.assignmentIntimationDownloadUrl
                  )
                }
                onRemove={() =>
                  void removeUploadedPdf(row.applicationId, "intimation")
                }
              />

              <UploadCell
                applicationId={row.applicationId}
                kind="status"
                filename={
                  row.pendingStatusFilename ??
                  row.statusOfApplicationPdfFilename ??
                  null
                }
                downloadUrl={row.statusOfApplicationDownloadUrl}
                uploadedFilename={row.statusOfApplicationPdfFilename}
                uploading={
                  !!uploadingKeys[rowKey(row.applicationId, "status")]
                }
                onFileSelected={(file) =>
                  void uploadFile(row.applicationId, "status", file)
                }
                onPreview={() =>
                  openPreview(
                    row.statusOfApplicationPdfFilename,
                    row.statusOfApplicationDownloadUrl
                  )
                }
                onDownload={() =>
                  void handleDownload(
                    row.statusOfApplicationPdfFilename,
                    row.statusOfApplicationDownloadUrl
                  )
                }
                onRemove={() =>
                  void removeUploadedPdf(row.applicationId, "status")
                }
              />
            </div>
          ))}
        </div>
      </div>

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </form>
  );
}

type UploadCellProps = {
  applicationId: string;
  kind: DocumentKind;
  filename: string | null;
  downloadUrl: string | null;
  uploadedFilename: string | null;
  uploading: boolean;
  onFileSelected: (file: File) => void;
  onPreview: () => void;
  onDownload: () => void;
  onRemove: () => void;
};

function UploadCell({
  applicationId,
  kind,
  filename,
  downloadUrl,
  uploadedFilename,
  uploading,
  onFileSelected,
  onPreview,
  onDownload,
  onRemove,
}: UploadCellProps) {
  const inputId = `assignment-status-${kind}-input-${applicationId}`;
  const hasFile = !!filename;
  const canPreview = !!downloadUrl && !!uploadedFilename;

  return (
    <div className="min-w-0">
      {hasFile ? (
        <div className="flex items-center justify-between gap-2 h-9 px-3 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]">
          <span
            className="text-[12px] text-[#16A34A] truncate min-w-0 flex-1"
            title={filename ?? ""}
          >
            {uploading ? "Uploading…" : filename}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            {canPreview && (
              <button
                type="button"
                onClick={onPreview}
                className="px-2 h-7 rounded-[6px] border border-[#D5D3E0] bg-white text-[11px] font-medium text-[#2E3037] hover:bg-gray-50"
              >
                Preview
              </button>
            )}
            {canPreview && (
              <button
                type="button"
                onClick={onDownload}
                className="px-2 h-7 rounded-[6px] border border-[#D5D3E0] bg-white text-[11px] font-medium text-[#2E3037] hover:bg-gray-50"
              >
                Download
              </button>
            )}
            <button
              type="button"
              disabled={uploading}
              onClick={() => document.getElementById(inputId)?.click()}
              className="px-2 h-7 rounded-[6px] border border-[#D5D3E0] bg-white text-[11px] font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Replace
            </button>
            <button
              type="button"
              aria-label={`Remove ${filename}`}
              onClick={onRemove}
              disabled={uploading}
              className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
              title="Remove"
            >
              <HiOutlineX className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={uploading}
          onClick={() => document.getElementById(inputId)?.click()}
          className="h-9 w-full flex items-center justify-center rounded-[8px] bg-white border border-[#D5D3E0] capitalize text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
            fontSize: "12px",
            fontWeight: 500,
            lineHeight: "14px",
            letterSpacing: "0.04em",
          }}
        >
          {uploading ? "Uploading…" : "Select File"}
        </button>
      )}

      <input
        id={inputId}
        type="file"
        accept={ACCEPTED_TYPES}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFileSelected(file);
          e.currentTarget.value = "";
        }}
      />
    </div>
  );
}

function mergeApplications(
  prev: RowState[],
  next: AssignmentStatusApplicationItem[]
): RowState[] {
  const prevById = new Map(prev.map((a) => [a.applicationId, a]));
  return next.map((app) => {
    const cached = prevById.get(app.applicationId);
    return {
      ...app,
      pendingIntimationFilename: cached?.pendingIntimationFilename ?? null,
      pendingStatusFilename: cached?.pendingStatusFilename ?? null,
    };
  });
}
